// ============================================
// Path: /frontend/src/components/auth/LoginForm.jsx
// Purpose: Login form component with email/password and forgot password
// ============================================

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/useToast';
import { Mail, KeyRound, Loader2, Eye, EyeOff } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { authAPI } from '../../api/auth.api';

export function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login({ email, password });
      toast({
        title: 'Success',
        description: 'Logged in successfully.',
      });
      navigate('/');
    } catch (error) {
      const status = error.response?.status;
      const message =
        error.response?.data?.message ||
        (status === 429
          ? 'Too many login attempts, please try again later.'
          : 'Invalid email or password');

      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      toast({
        variant: 'destructive',
        title: 'Email required',
        description: 'Please enter your email address to reset your password.',
      });
      return;
    }

    setResetLoading(true);
    try {
      await authAPI.forgotPassword(resetEmail);
      toast({
        title: 'Password Reset Email Sent',
        description: 'Please check your inbox for a link to reset your password.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.message || 'Could not send password reset email.',
      });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-4">
      <CardHeader className="text-center space-y-1.5 sm:space-y-2 p-4 sm:p-6">
        <CardTitle className="text-xl sm:text-2xl md:text-3xl font-bold">Welcome Back</CardTitle>
        <CardDescription className="text-xs sm:text-sm md:text-base">
          Enter your credentials to access your account
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="email" className="text-xs sm:text-sm">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                required
                className="pl-9 sm:pl-10 h-9 sm:h-10 text-xs sm:text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5 sm:space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs sm:text-sm">Password</Label>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="link"
                    className="text-[10px] sm:text-xs md:text-sm font-medium text-primary hover:underline p-0 h-auto"
                  >
                    Forgot password?
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="w-[calc(100%-2rem)] max-w-md mx-4">
                  <AlertDialogHeader className="space-y-1.5 sm:space-y-2">
                    <AlertDialogTitle className="text-base sm:text-lg md:text-xl">Reset Your Password</AlertDialogTitle>
                    <AlertDialogDescription className="text-xs sm:text-sm md:text-base">
                      Enter your email address below. If an account exists, we'll send you a link
                      to reset your password.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="name@example.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="h-9 sm:h-10 text-xs sm:text-sm"
                  />
                  <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
                    <AlertDialogCancel className="text-xs sm:text-sm h-9 sm:h-10 m-0">Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handlePasswordReset} 
                      disabled={resetLoading}
                      className="text-xs sm:text-sm h-9 sm:h-10 font-medium m-0"
                    >
                      {resetLoading && <Loader2 className="animate-spin mr-2 h-3 w-3 sm:h-4 sm:w-4" />}
                      Send Reset Link
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                required
                className="pl-9 sm:pl-10 pr-10 h-9 sm:h-10 text-xs sm:text-sm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                ) : (
                  <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
              </button>
            </div>
          </div>
          <Button 
            type="submit" 
            className="w-full !mt-6 sm:!mt-8 font-semibold text-xs sm:text-sm h-10 sm:h-11" 
            disabled={loading}
          >
            {loading ? <Loader2 className="animate-spin h-4 w-4 sm:h-5 sm:w-5" /> : 'Login'}
          </Button>
        </form>
        <div className="mt-4 sm:mt-6 text-center text-[10px] sm:text-xs md:text-sm">
          Don't have an account?{' '}
          <Link 
            to="/register" 
            className="font-medium text-primary hover:underline transition-colors"
          >
            Sign up
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}